import {
  DropdownSelect,
  FieldWrapper,
  FlexBox,
  InputText,
  SelectItem,
} from '@dixa/lib-shared-habla';
import { ChangeEvent, useState } from 'react';
import { useDebounce } from 'react-use';

import {
  messengerLanguages,
  MessengerTranslatedLanguage,
} from 'components/settings/components/sections/dixaMessenger/views/appearance/components/generalSettings/languageSettings/languages';

import { TranslatedString } from '../../../../../../../../messengerSettings.types';

import styles from './translateString.module.scss';

type LanguageCodeWithName = {
  id: MessengerTranslatedLanguage;
  name: string;
};

const items: LanguageCodeWithName[] = messengerLanguages.map(
  ({ code, label }) => ({
    id: code,
    name: label,
  }),
);

const makeInputDescription = (value: string, maxCharacters: number): string => {
  const charactersLeft = Math.max(maxCharacters - value.length, 0);
  return `${charactersLeft} ${
    charactersLeft === 1 ? 'character' : 'characters'
  } left`;
};

type Props = {
  selectedLanguage: string;
  inputLabel: string;
  translatedString: TranslatedString;
  placeholder: string;
  onInputChange: (value: string) => void;
  onLanguageSelect: (lang: MessengerTranslatedLanguage) => void;
};

/**
 * Dropdown language selection with input field for adding / editing string
 * translations
 */
export function TranslateString({
  selectedLanguage,
  inputLabel,
  translatedString,
  placeholder,
  onLanguageSelect,
  onInputChange,
}: Props): JSX.Element {
  const selectedItem = items.find((x) => x.id === selectedLanguage);
  const [debouncedValue, setDebouncedValue] = useState(
    translatedString.translatedMap[selectedLanguage] ?? '',
  );
  useDebounce(() => onInputChange(debouncedValue), 150, [debouncedValue]);
  const inputDescription = makeInputDescription(
    debouncedValue,
    translatedString.maxLength,
  );

  const handleOnInputChange = (ev: ChangeEvent<HTMLInputElement>) => {
    const { value } = ev.target;
    if (value.length <= translatedString.maxLength) {
      setDebouncedValue(ev.target.value);
    } else {
      setDebouncedValue(
        ev.target.value.substring(0, translatedString.maxLength),
      );
    }
  };

  return (
    <FlexBox gap="300" alignItems="center" margin="300 0 0">
      <FieldWrapper label="Language" className={styles.fieldWrapper}>
        <DropdownSelect
          buttonProps={{
            className: styles.selectButton,
            styleText: false,
          }}
          items={items}
          itemComponent={SelectItem}
          selectedItems={selectedItem ? [selectedItem.id] : []}
          onSelect={(x) => onLanguageSelect(x.id)}
          noArrow={false}
        >
          {selectedItem ? selectedItem.name : 'Select Item'}
        </DropdownSelect>
      </FieldWrapper>
      <FieldWrapper
        label={inputLabel}
        description={inputDescription}
        className={styles.fieldWrapper}
      >
        <InputText
          className={styles.input}
          value={debouncedValue}
          onChange={handleOnInputChange}
          placeholder={placeholder}
        />
      </FieldWrapper>
    </FlexBox>
  );
}
